import { useState } from 'react';
import { Box } from 'lucide-react';
import { apiService, PackBlockBox, PackBlockDTO } from '@/services/api.service';
import { ConfigCatalog } from '@/components/config/ConfigCatalog';
import { BedrockModelViewer, Facing, OversizedOverlay } from '@/components/config/BedrockModelViewer';
import { BlockEditor } from '@/components/config/BlockEditor';
import { BoxEditor } from '@/components/config/BoxEditor';
import { OversizedBulkConvert } from '@/components/config/OversizedBulkConvert';
import {
  effectiveCells,
  effectiveFootprint,
  effectiveObject,
  initialOversizedDraft,
  OversizedDraft,
  toggleDraftCell,
  useGeometryAnalysis,
} from '@/components/config/OversizedSettings';
import styles from '@/components/config/PackDetail.module.scss';
import ovStyles from '@/components/config/Oversized.module.scss';

/** Remplissages invisibles des objets (créés par la conversion) : signalés comme tels dans la liste. */
const OVERSIZED_PARTS = new Set(['propsi:oversized_part', 'propsi:oversized_part_passable']);

export function BlocksPage() {
  return (
    <ConfigCatalog<PackBlockDTO>
      title="Blocks"
      icon={Box}
      kind="block"
      load={async (g) => (await apiService.getPackBlocks(g)).data?.blocks ?? []}
      keyOf={(b) => b.identifier}
      identifierOf={(b) => b.identifier}
      subtitleOf={(b) => b.pack}
      thumbnailOf={(b) => b.thumbnail}
      matches={(b, q) =>
        b.identifier.toLowerCase().includes(q) ||
        b.pack.toLowerCase().includes(q) ||
        (b.geometry || '').toLowerCase().includes(q)
      }
      statusOf={(b) =>
        b.oversized ? (
          <span className={`${styles.chip} ${ovStyles.objectChip}`} title={`Objet affiché par ${b.oversized.displayEntity}`}>Objet</span>
        ) : OVERSIZED_PARTS.has(b.identifier) ? (
          <span className={styles.chip} title="Remplissage invisible des objets">Remplissage</span>
        ) : (
          <span className={`${styles.chip} ${b.geometry ? styles.chipPrimary : ''}`}>
            {b.geometry ? '3D' : b.sourceType === 'resource_only' ? 'RP' : 'BP'}
          </span>
        )
      }
      sourceOf={(b) => (b.sourceType === 'block' ? b.source : null)}
      headerExtra={({ groups, selectedGroupIds, reload }) => {
        // Seulement si un groupe affiché a le drapeau « gros blocs de déco ».
        const flagged = groups.filter((g) => g.oversizedBlocks && selectedGroupIds.includes(g.id));
        return flagged.length > 0 ? <OversizedBulkConvert groups={flagged} onDone={reload} /> : null;
      }}
      renderDetail={(b, g, reload, group, visible) => (
        <BlockDetail
          block={b}
          groupId={g}
          reload={reload}
          readonlyResource={!!group?.resourceReadonly}
          oversizedEnabled={!!group?.oversizedBlocks}
          visible={visible !== false}
        />
      )}
    />
  );
}

const FACINGS: { id: Facing; label: string; title: string }[] = [
  { id: 'N', label: 'N', title: 'Nord (orientation des cellules enregistrées)' },
  { id: 'E', label: 'E', title: 'Est' },
  { id: 'S', label: 'S', title: 'Sud' },
  { id: 'W', label: 'O', title: 'Ouest' },
];

function BlockDetail({ block, groupId, reload, readonlyResource, oversizedEnabled, visible }: {
  block: PackBlockDTO;
  groupId: string;
  reload: () => void;
  readonlyResource: boolean;
  oversizedEnabled: boolean;
  visible: boolean;
}) {
  const primary = block.thumbnail || Object.values(block.textures || {})[0] || null;
  const [collision, setCollision] = useState<PackBlockBox>(block.collision || { mode: 'full', origin: null, size: null });
  const [selection, setSelection] = useState<PackBlockBox>(block.selection || { mode: 'full', origin: null, size: null });
  const [preview, setPreview] = useState<{
    textureUrl: string | null;
    texturePath: string | null;
    geometryText: string | null;
    geometryFile: string | null;
    geometryId: string;
  }>({ textureUrl: null, texturePath: null, geometryText: null, geometryFile: null, geometryId: block.geometry || '' });
  const hasTexOverride = !!(preview.textureUrl || preview.texturePath);

  // Gros bloc de déco : réglage partagé entre l'éditeur (enregistrement) et l'aperçu (cellules).
  const [ovDraft, setOvDraft] = useState<OversizedDraft>(() => initialOversizedDraft(block));
  const [facing, setFacing] = useState<Facing>('N');
  // Géométrie choisie dans l'éditeur (vide = cube plein, rien à analyser) ou fichier en attente.
  const { analysis, loading, error } = useGeometryAnalysis(groupId, oversizedEnabled, preview.geometryId, preview.geometryText, ovDraft.threshold);
  const isObject = effectiveObject(ovDraft, analysis);
  const overlay: OversizedOverlay | null = oversizedEnabled && analysis && !analysis.empty
    ? {
        worldMin: analysis.worldMin,
        worldMax: analysis.worldMax,
        tooBig: analysis.strictVerdict === 'too_big',
        cells: analysis.cells,
        selected: isObject && effectiveFootprint(ovDraft, analysis) === 'cells' ? effectiveCells(ovDraft, analysis) : [],
        onToggleCell: isObject && !readonlyResource ? (c) => setOvDraft((d) => toggleDraftCell(d, analysis, c)) : undefined,
      }
    : null;

  return (
    <div>
      <div className={styles.head}>
        <div>
          <div className={styles.name}>{block.identifier}</div>
          <div className={styles.pack}>{block.pack}</div>
        </div>
        <div className={styles.chips}>
          {block.oversized && <span className={`${styles.chip} ${ovStyles.objectChip}`}>Objet</span>}
          {block.geometry && <span className={`${styles.chip} ${styles.chipPrimary}`}>Géométrie</span>}
          {block.renderMethod && <span className={styles.chip}>{block.renderMethod}</span>}
        </div>
      </div>

      {visible && (
        <div className={styles.viewerWrap}>
          <BedrockModelViewer
            groupId={groupId}
            geometryFile={preview.geometryText ? null : preview.geometryFile ?? block.geometryFile}
            geometryId={preview.geometryId || block.geometry}
            texturePath={preview.textureUrl ? null : preview.texturePath ?? primary}
            faceTextures={hasTexOverride ? undefined : block.textures}
            renderMethod={block.renderMethod}
            overrideTextureUrl={preview.textureUrl}
            overrideGeometryText={preview.geometryText}
            showGround
            collisionBox={collision}
            selectionBox={selection}
            worldFrame={oversizedEnabled}
            facing={facing}
            oversized={overlay}
          />
          {oversizedEnabled && (
            <div className={ovStyles.facingBar} title="Orientation de l'aperçu : modèle, box et empreinte tournent comme en jeu">
              <span className={ovStyles.facingLabel}>Orientation</span>
              {FACINGS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  title={f.title}
                  className={`${ovStyles.facingBtn} ${facing === f.id ? ovStyles.facingActive : ''}`}
                  onClick={() => setFacing(f.id)}
                >
                  {f.label}
                </button>
              ))}
            </div>
          )}
          <div className={styles.boxOverlay}>
            <BoxEditor label="Box de collision" color="#ff5555" box={collision} onChange={setCollision} />
            <BoxEditor label="Hitbox (sélection)" color="#38bdf8" box={selection} onChange={setSelection} />
          </div>
        </div>
      )}

      <BlockEditor
        block={block}
        groupId={groupId}
        onSaved={reload}
        collision={collision}
        selection={selection}
        readonlyResource={readonlyResource}
        onPreviewOverride={setPreview}
        oversized={oversizedEnabled ? { draft: ovDraft, setDraft: setOvDraft, analysis, loading, error } : undefined}
      />
    </div>
  );
}
